import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { useAuthor } from '@/hooks/useAuthor';
import { getDisplayName } from '@/lib/getDisplayName';
import { sanitizeUrl } from '@/lib/sanitizeUrl';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { timeAgo, fullDate } from '@/lib/jumble';

export function pubkeyToNpub(pubkey: string): string {
  try {
    return nip19.npubEncode(pubkey);
  } catch {
    return pubkey;
  }
}

export function UserAvatar({
  pubkey,
  size = 'md',
  className,
}: {
  pubkey: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}) {
  const author = useAuthor(pubkey);
  const picture = author.data?.metadata?.picture;
  const name = getDisplayName(author.data?.metadata, pubkey);
  const sizes = { xs: 'h-6 w-6', sm: 'h-8 w-8', md: 'h-10 w-10', lg: 'h-12 w-12', xl: 'h-20 w-20' };
  const safePic = picture ? sanitizeUrl(picture) : undefined;
  return (
    <Avatar className={cn(sizes[size], 'shrink-0', className)}>
      {safePic && <AvatarImage src={safePic} alt={name} />}
      <AvatarFallback className="bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white text-xs font-bold">
        {name.slice(0, 2).toUpperCase()}
      </AvatarFallback>
    </Avatar>
  );
}

export function Username({
  pubkey,
  className,
  showNip05 = false,
}: {
  pubkey: string;
  className?: string;
  showNip05?: boolean;
}) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const name = getDisplayName(metadata, pubkey);
  return (
    <Link
      to={`/${pubkeyToNpub(pubkey)}`}
      onClick={(e) => e.stopPropagation()}
      className={cn('font-semibold hover:underline truncate', className)}
    >
      {name}
      {showNip05 && metadata?.nip05 && (
        <span className="ml-1 font-normal text-muted-foreground">· {metadata.nip05}</span>
      )}
    </Link>
  );
}

export function Timestamp({ createdAt, className }: { createdAt: number; className?: string }) {
  return (
    <span title={fullDate(createdAt)} className={cn('text-muted-foreground whitespace-nowrap', className)}>
      {timeAgo(createdAt)}
    </span>
  );
}
